import React from 'react'
import { SafetyCertificateOutlined, BulbOutlined } from '@ant-design/icons'

/**
 * The two reliability treatments an author actually sees.
 *
 * The compiler still produces four tiers and the wire still carries them; this is a DISPLAY
 * collapse. Four turned out to be more than an author can act on — told a rule is "Checked"
 * rather than "Guaranteed" or "Needs review", there is no move for them to make, and the
 * distinction is engineering information carrying no user decision.
 *
 * @see `directive-tier-view.ts` in sailfish — the same collapse, server-side, for `tier_view`.
 */
export type BrandVoiceDisplayTier = 'guaranteed' | 'guidance'

/**
 * The compiler's own tiers. Still the internal vocabulary because SPAN KEYS are built from these
 * names (`checked-0`, `needsReview-2`, …) and the textarea↔row highlight wiring resolves a row's
 * tier by reading that prefix. Renaming them to collapse the display would mean rewriting that
 * wiring for no user-visible gain.
 */
export type BrandVoiceTierKey = 'guaranteed' | 'checked' | 'guidance' | 'needsReview'

/** Which of the two an internal tier is shown as. */
export const DISPLAY_TIER_FOR: Record<BrandVoiceTierKey, BrandVoiceDisplayTier> = {
    guaranteed: 'guaranteed',
    checked: 'guidance',
    guidance: 'guidance',
    needsReview: 'guidance',
} as const

/**
 * Icon/color identity per DISPLAY tier — the single source the decomposition, the summary chips
 * and the textarea wash all render from, so a settings-page glance and the full drawer always
 * agree on what "Guaranteed" looks like.
 */
export const BRAND_VOICE_DISPLAY_TIER_VISUAL: Record<BrandVoiceDisplayTier, { icon: React.ReactNode; color: string }> =
    {
        guaranteed: { icon: <SafetyCertificateOutlined />, color: 'var(--ant-color-success, #60c3ac)' },
        guidance: { icon: <BulbOutlined />, color: 'var(--ant-color-text-tertiary, #838f9a)' },
    }

/**
 * Per-internal-tier visual, resolved through {@link DISPLAY_TIER_FOR}.
 *
 * Keyed by the internal tier so existing callers (span keys, authoring tips) keep working, but
 * `checked` and `needsReview` deliberately resolve to the SAME icon and color as `guidance`.
 * They render inside one section now, and three colors under one heading would read as three
 * categories the heading is failing to name.
 */
export const BRAND_VOICE_TIER_VISUAL: Record<BrandVoiceTierKey, { icon: React.ReactNode; color: string }> = {
    guaranteed: BRAND_VOICE_DISPLAY_TIER_VISUAL.guaranteed,
    checked: BRAND_VOICE_DISPLAY_TIER_VISUAL.guidance,
    guidance: BRAND_VOICE_DISPLAY_TIER_VISUAL.guidance,
    needsReview: BRAND_VOICE_DISPLAY_TIER_VISUAL.guidance,
}

/** Order the two are offered in, and the order a default selection falls through. */
export const DISPLAY_TIER_ORDER: BrandVoiceDisplayTier[] = ['guaranteed', 'guidance']

/**
 * The display tier a span key belongs to.
 *
 * Keys are `${internalTier}-${index}`; the Guaranteed rows use their own key shape but always
 * start `guaranteed`. Anything unrecognized falls to `guidance` — the tier that promises less,
 * so a key shape this does not know about can never be presented as a guarantee.
 */
export function displayTierForSpanKey(key: string): BrandVoiceDisplayTier {
    const internal = key.split('-')[0] as BrandVoiceTierKey
    return DISPLAY_TIER_FOR[internal] ?? 'guidance'
}
